import { useLocalStorageState } from 'ahooks'
import { Alert, Modal } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { SpecialProjectFormLocalStorageKey } from '@/schemas'

export const SpecialProjectLocalStorageModal = ({
  localStorageKey,
  onSetForm,
}: {
  localStorageKey: SpecialProjectFormLocalStorageKey
  onSetForm: (value: any) => void
}) => {
  const [formCache, setFormCache] = useLocalStorageState<{ localCacheExpireTime?: string }>(localStorageKey)
  const [open, setOpen] = React.useState(false)
  const { t } = useTranslation()

  React.useEffect(() => {
    const { localCacheExpireTime } = formCache || {}
    if (localCacheExpireTime && moment(localCacheExpireTime).clone().add(7, 'day').isSameOrBefore(moment(), 'day')) {
      setFormCache(undefined)
    } else {
      setOpen(!!formCache)
    }
  }, [formCache])

  return (
    <Modal
      visible={open}
      title={t('special-project.form.create.retrieve.title')}
      onOk={() => {
        onSetForm(formCache)
        setOpen(false)
      }}
      cancelText={t('special-project.form.create.retrieve.cancel')}
      okText={t('special-project.form.create.retrieve.ok')}
      cancelButtonProps={{
        type: 'text',
        danger: true,
      }}
      onCancel={() => {
        setFormCache(undefined)
        setOpen(false)
      }}
    >
      <Alert message={t('special-project.form.create.retrieve.description')} type='info' />
    </Modal>
  )
}

export const useOnSetSpecialProjectCache = (localStorageKey: SpecialProjectFormLocalStorageKey) => {
  const [, setFormCache] = useLocalStorageState<{ localCacheExpireTime?: string }>(localStorageKey)
  function onSetCache<T extends { localCacheExpireTime?: string }>(fieldData: T) {
    setFormCache((prev: any) => {
      const prevLocalCacheExpireTime = prev?.localCacheExpireTime
      const data = { ...fieldData }
      if (!prevLocalCacheExpireTime || moment(prevLocalCacheExpireTime).add(2, 'hour').isBefore(moment(), 'hour')) {
        data.localCacheExpireTime = moment().toISOString()
      }
      return { ...prev, ...data }
    })
  }

  return {
    onSetCache,
    setFormCache,
  }
}
